/**
 * LEADS-CANONICAL-FOLD-V1 (V5-P10) -- Owner Leads LIST route
 * (`/dashboard/owner/leads`) as a REDIRECT SHIM into the canonical.
 *
 * Founder ruling 2026-07-06 (duplicate-screen catch): there is ONE canonical
 * lead list, `/dashboard/leads` (LeadsWorkspacePage -- the Lead-360 directory
 * with system views + clinic picker). The older owner list here
 * (LeadsListPage, 04-SURFACE-FATES row 11) is SUPERSEDED. The owner is in the
 * Leads Workspace audience, so me.leadsWorkspaceEnabled is present and this
 * route REDIRECTS to the canonical, so the owner never renders the second list.
 *
 * The internal-surface env flag was retired by RECEPTIONIST-EXPERIENCE-STITCH-V1;
 * me.leadsWorkspaceEnabled is now a flag-free AUDIENCE signal (always present
 * for the owner). The legacy owner LeadsListPage below is a vestigial fallback
 * for the transient me-absent case only. This is the SAME pattern the sibling
 * detail route ships (owner/LeadDetailPage.tsx, `decideOwnerLeadDetail`; V1-P3),
 * so the list and the detail fold identically.
 *
 * Presentation only: the workspace endpoint's role + clinic scope stays the
 * security boundary; this shim grants nothing and widens no audience.
 */

import { Navigate } from 'react-router-dom';

import { useDashboardMeContext } from '@/dashboard/me-context';
import LeadsListPage from '@/dashboard/owner/LeadsListPage';

/** The ONE canonical lead list (04-SURFACE-FATES rows 18/19). */
export const CANONICAL_LEADS_PATH = '/dashboard/leads';

/**
 * Pure fold decision for the owner list route (unit-testable without jsdom,
 * mirroring `decideOwnerLeadDetail`):
 *   - `wait`     : `me` still loading -> render a loading shell (do not fetch).
 *   - `redirect` : the canonical Leads Workspace is live for this owner
 *                  -> go to the ONE canonical list.
 *   - `legacy`   : kill-switch OFF (or no `me`) -> render the legacy owner list
 *                  (byte-identical to today; never a homePath bounce).
 */
export type OwnerLeadsListDecision =
  | { kind: 'wait' }
  | { kind: 'legacy' }
  | { kind: 'redirect'; to: string };

export function decideOwnerLeadsList(state: {
  loading: boolean;
  leadsWorkspaceEnabled?: boolean;
}): OwnerLeadsListDecision {
  if (state.loading) return { kind: 'wait' };
  if (state.leadsWorkspaceEnabled === true) {
    return { kind: 'redirect', to: CANONICAL_LEADS_PATH };
  }
  return { kind: 'legacy' };
}

/**
 * Flag-aware fold shim. Route-singularity when the canonical surface is live;
 * the legacy owner list as the kill-switch when it is not.
 */
export default function OwnerLeadsListRoute(): JSX.Element {
  const meCtx = useDashboardMeContext();

  const decision = decideOwnerLeadsList({
    loading: meCtx?.loading === true,
    leadsWorkspaceEnabled: meCtx?.me?.leadsWorkspaceEnabled,
  });

  switch (decision.kind) {
    case 'wait':
      // Brief loading shell rather than briefly rendering (and fetching) the
      // legacy list only to redirect a moment later.
      return (
        <section className="leads-list">
          <div className="page-header">
            <h1>Leads</h1>
          </div>
          <div className="loading">Loading&hellip;</div>
        </section>
      );
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'legacy':
      return <LeadsListPage />;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return <LeadsListPage />;
    }
  }
}
